import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertTriangle, CloudRain, Loader2, Settings } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { useCostingSettings } from '@/hooks/costing/useCostingSettings';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useIsFieldHidden } from '@/hooks/usePermissions';
import { apiGet, operationsClient } from '@/lib/api';
import {
  billingAlertEndpoints,
  costAnalyticsReportEndpoints,
  jobEndpoints,
} from '@/lib/api/endpoints';
import type { JobListResponse } from '@/types/job';
import {
  AnalyticsReportShell,
  MetricCard,
  ReportDateRangeControl,
  ReportSection,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useReportPeriod,
  type ScorecardColumn,
} from '@/components/organisms/analytics-reports';

interface BvaOverview {
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  budgetLaborHours: number;
  actualLaborHours: number;
  visitCount: number;
}

interface BvaContractRow {
  jobId: string;
  label: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BvaPropertyRow {
  property: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BvaServiceRow {
  productionKitId: string;
  kitName: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BudgetVsActualReport {
  period: { from: string | null; to: string | null };
  overview: BvaOverview;
  byContract: BvaContractRow[];
  byProperty: BvaPropertyRow[];
  byService: BvaServiceRow[];
}

interface BillingAlert {
  id: string;
  jobId?: string | null;
  kind: string;
  severity: string;
  message: string;
  reason?: string | null;
  occurrenceCount: number;
  lastSeenAt: string;
  createdAt: string;
  resolvedAt?: string | null;
}

interface WeatherImpactRow {
  bucket: string;
  held: number;
  skipped: number;
  madeUpFlex: number;
  madeUpReabsorb: number;
  stillHeld: number;
  estRevenueImpact: number;
}

interface WeatherImpactReport {
  period: { from: string | null; to: string | null };
  groupBy: 'territory' | 'month' | 'route';
  rows: WeatherImpactRow[];
}

export const Route = createFileRoute('/_dashboard/dashboard/costing/reports')({
  component: CostingReportsPage,
});

const COSTING_PERIOD_STORAGE_KEY = 'analytics.costing.budgetVsActual';

function sharedColumns<
  R extends {
    budgetCost: number;
    actualCost: number;
    costVariance: number;
    revenue: number;
    margin: number;
    marginPct: number | null;
    visits: number;
  },
>(
  firstKey: keyof R & string,
  firstHeader: string,
  firstValue: (r: R) => string,
  showMoneyColumns: boolean,
): ScorecardColumn<R>[] {
  const columns: ScorecardColumn<R>[] = [
    { key: firstKey, header: firstHeader, type: 'text', value: firstValue },
    { key: 'visits', header: 'Visits', type: 'number', value: (r) => r.visits },
  ];

  if (!showMoneyColumns) {
    return [
      ...columns,
      { key: 'marginPct', header: 'Margin', type: 'percent', value: (r) => r.marginPct ?? 0 },
    ];
  }

  return [
    ...columns,
    {
      key: 'budgetCost',
      header: 'Budget',
      type: 'currency',
      value: (r) => r.budgetCost,
      total: (rows) => rows.reduce((s, r) => s + r.budgetCost, 0),
    },
    {
      key: 'actualCost',
      header: 'Actual',
      type: 'currency',
      value: (r) => r.actualCost,
      total: (rows) => rows.reduce((s, r) => s + r.actualCost, 0),
    },
    {
      key: 'costVariance',
      header: 'Variance',
      type: 'currency',
      value: (r) => r.costVariance,
      total: (rows) => rows.reduce((s, r) => s + r.costVariance, 0),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      type: 'currency',
      value: (r) => r.revenue,
      total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
    },
    { key: 'marginPct', header: 'Margin', type: 'percent', value: (r) => r.marginPct ?? 0 },
  ];
}

function weatherColumns(
  bucketHeader: string,
  profitabilityHidden: boolean,
): ScorecardColumn<WeatherImpactRow>[] {
  return [
    {
      key: 'bucket',
      header: bucketHeader,
      type: 'text',
      value: (r) => r.bucket,
      render: (r) => (
        <span className="inline-flex items-center gap-2">
          <CloudRain className="size-4 text-muted-foreground" />
          {r.bucket}
        </span>
      ),
    },
    { key: 'held', header: 'Held', type: 'number', value: (r) => r.held },
    { key: 'skipped', header: 'Skipped', type: 'number', value: (r) => r.skipped },
    { key: 'madeUpFlex', header: 'Flex', type: 'number', value: (r) => r.madeUpFlex },
    {
      key: 'madeUpReabsorb',
      header: 'Reabsorbed',
      type: 'number',
      value: (r) => r.madeUpReabsorb,
    },
    { key: 'stillHeld', header: 'Still held', type: 'number', value: (r) => r.stillHeld },
    ...(profitabilityHidden
      ? []
      : [
          {
            key: 'estRevenueImpact',
            header: 'Revenue impact',
            type: 'currency' as const,
            value: (r: WeatherImpactRow) => r.estRevenueImpact,
            total: (rows: WeatherImpactRow[]) =>
              rows.reduce((sum, r) => sum + r.estRevenueImpact, 0),
          },
        ]),
  ];
}

function CostingReportsPage() {
  const period = useReportPeriod(COSTING_PERIOD_STORAGE_KEY);
  const params = { from: period.from, to: period.to };
  const storedCostingPeriod = hasStoredCostingPeriod();
  const costingSettingsQuery = useCostingSettings();
  const profitabilityHidden = useIsFieldHidden('PROFITABILITY');
  const canViewProfitability = !profitabilityHidden;
  const reportingEnabled = costingSettingsQuery.data?.enableReporting === true;
  const seasonDefaultsQuery = useApiQuery<JobListResponse>({
    queryKey: ['costing', 'reports', 'weather-season-defaults'],
    queryFn: async () => {
      const statuses = ['ACTIVE', 'PAST_DUE', 'SUSPENDED'] as const;
      const pages = await Promise.all(
        statuses.map((recurringStatus) =>
          apiGet<JobListResponse>(operationsClient, jobEndpoints.list(), {
            params: {
              recurringStatus,
              page: 1,
              limit: 100,
              sortBy: 'updatedAt',
              sortDir: 'desc',
            },
          }),
        ),
      );
      const [first] = pages;
      return {
        ...(first ?? { page: 1, limit: 100, totalPages: 1 }),
        data: pages.flatMap((page) => page.data),
        total: pages.reduce((sum, page) => sum + page.total, 0),
      };
    },
    enabled: reportingEnabled && canViewProfitability && !storedCostingPeriod,
    showErrorToast: false,
  });
  const seasonWeatherRange = resolveSeriesSeasonRange(seasonDefaultsQuery.data?.data ?? []);
  const weatherParams = storedCostingPeriod
    ? params
    : (seasonWeatherRange ?? currentQuarterRange());
  const weatherDefaultReady =
    storedCostingPeriod || seasonDefaultsQuery.isFetched || seasonDefaultsQuery.isError;

  const reportQuery = useApiQuery<BudgetVsActualReport>({
    queryKey: ['costing', 'reports', 'budget-vs-actual', params],
    queryFn: () =>
      apiGet<BudgetVsActualReport>(
        operationsClient,
        costAnalyticsReportEndpoints.budgetVsActual(params),
      ),
    enabled: reportingEnabled && canViewProfitability,
    showErrorToast: false,
  });
  const alertsQuery = useApiQuery<BillingAlert[]>({
    queryKey: ['billing', 'alerts', 'cost-variance', 'includeResolved'],
    queryFn: () =>
      apiGet<BillingAlert[]>(
        operationsClient,
        billingAlertEndpoints.list({ includeResolved: true }),
      ),
    enabled: reportingEnabled && canViewProfitability,
    showErrorToast: false,
  });
  const weatherImpactQuery = useApiQuery<WeatherImpactReport>({
    queryKey: ['costing', 'reports', 'weather-impact', weatherParams],
    queryFn: () =>
      apiGet<WeatherImpactReport>(
        operationsClient,
        costAnalyticsReportEndpoints.weatherImpact({ ...weatherParams, groupBy: 'month' }),
      ),
    enabled: reportingEnabled && canViewProfitability && weatherDefaultReady,
    showErrorToast: false,
  });
  const weatherTerritoryImpactQuery = useApiQuery<WeatherImpactReport>({
    queryKey: ['costing', 'reports', 'weather-impact-territory', weatherParams],
    queryFn: () =>
      apiGet<WeatherImpactReport>(
        operationsClient,
        costAnalyticsReportEndpoints.weatherImpact({ ...weatherParams, groupBy: 'territory' }),
      ),
    enabled: reportingEnabled && canViewProfitability && weatherDefaultReady,
    showErrorToast: false,
  });

  const data = reportQuery.data;
  const overview = data?.overview;
  const costAlerts = (alertsQuery.data ?? []).filter((alert) => alert.kind === 'COST_VARIANCE');
  const openCostAlerts = costAlerts.filter((alert) => !alert.resolvedAt);
  const resolvedCostAlerts = costAlerts.filter((alert) => alert.resolvedAt);
  const moneyValue = (value: number | null | undefined) =>
    profitabilityHidden ? '—' : formatCurrency(value);

  if (costingSettingsQuery.isLoading) {
    return (
      <AnalyticsReportShell
        title="Costing Reports"
        subtitle="Budget vs actual cost performance and open variance alerts"
        backTo="/dashboard/jobs"
        exportDisabled
      >
        <div className="flex min-h-64 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading costing settings
        </div>
      </AnalyticsReportShell>
    );
  }

  if (!reportingEnabled) {
    return (
      <AnalyticsReportShell
        title="Costing Reports"
        subtitle="Budget vs actual cost performance and open variance alerts"
        backTo="/dashboard/jobs"
        exportDisabled
      >
        <ReportSection
          title="Reporting disabled"
          description="Enable tenant-wide costing reports before opening budget vs actual reporting."
        >
          <Button asChild>
            <Link to="/dashboard/settings/contracts/costing">
              <Settings className="size-4" />
              Open costing settings
            </Link>
          </Button>
        </ReportSection>
      </AnalyticsReportShell>
    );
  }

  return (
    <AnalyticsReportShell
      title="Costing Reports"
      subtitle="Budget vs actual cost performance and open variance alerts"
      backTo="/dashboard/jobs"
      exportDisabled
      actions={<ReportDateRangeControl period={period} />}
    >
      <ReportSection
        title="Overview"
        description="Completed, budgeted visits in the selected range."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <MetricCard
            title="Visits"
            value={overview ? formatInteger(overview.visitCount) : '--'}
            isLoading={reportQuery.isLoading}
          />
          <MetricCard
            title="Budget Cost"
            value={overview ? moneyValue(overview.budgetCost) : '--'}
            isLoading={reportQuery.isLoading}
          />
          <MetricCard
            title="Actual Cost"
            value={overview ? moneyValue(overview.actualCost) : '--'}
            isLoading={reportQuery.isLoading}
            deltaPct={
              profitabilityHidden
                ? null
                : computeDeltaPct(overview?.actualCost, overview?.budgetCost)
            }
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="Cost Variance"
            value={overview ? moneyValue(overview.costVariance) : '--'}
            isLoading={reportQuery.isLoading}
          />
          <MetricCard
            title="Revenue"
            value={overview ? moneyValue(overview.revenue) : '--'}
            isLoading={reportQuery.isLoading}
          />
          <MetricCard
            title="Margin"
            value={overview ? moneyValue(overview.margin) : '--'}
            isLoading={reportQuery.isLoading}
          />
          <MetricCard
            title="Margin %"
            value={overview?.marginPct != null ? formatPercent(overview.marginPct) : '--'}
            isLoading={reportQuery.isLoading}
          />
        </div>
      </ReportSection>

      <ReportSection title="Open variance alerts">
        <div className="rounded-md border border-border bg-background">
          {alertsQuery.isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading alerts...</div>
          ) : openCostAlerts.length === 0 ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              No open cost variance alerts
            </div>
          ) : (
            <div className="divide-y divide-border">
              {openCostAlerts.slice(0, 8).map((alert) => (
                <div key={alert.id} className="flex items-start justify-between gap-4 p-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={alert.severity === 'critical' ? 'destructive' : 'secondary'}>
                        {alert.severity}
                      </Badge>
                      {alert.reason ? (
                        <span className="text-xs text-muted-foreground">{alert.reason}</span>
                      ) : null}
                    </div>
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">
                      Seen {alert.occurrenceCount} time{alert.occurrenceCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  {alert.jobId ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/dashboard/jobs/$id" params={{ id: alert.jobId }}>
                        Open job
                      </Link>
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </ReportSection>

      <ReportSection
        title="Resolved variance history"
        description="Closed cost variance alerts in the selected tenant feed."
      >
        <div className="rounded-md border border-border bg-background">
          {alertsQuery.isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading history...</div>
          ) : resolvedCostAlerts.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              No resolved cost variance alerts
            </div>
          ) : (
            <div className="divide-y divide-border">
              {resolvedCostAlerts.slice(0, 8).map((alert) => (
                <div key={alert.id} className="flex items-start justify-between gap-4 p-4">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">resolved</Badge>
                      {alert.resolvedAt ? (
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(alert.resolvedAt)}
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm font-medium">{alert.message}</p>
                    {alert.reason ? (
                      <p className="text-xs text-muted-foreground">{alert.reason}</p>
                    ) : null}
                  </div>
                  {alert.jobId ? (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/dashboard/jobs/$id" params={{ id: alert.jobId }}>
                        Open job
                      </Link>
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </ReportSection>

      <ReportSection
        title="Weather impact"
        description="Held, skipped, and recovered visits in the selected range."
      >
        <div className="grid gap-4 xl:grid-cols-2">
          <ScorecardTable<WeatherImpactRow>
            rows={weatherImpactQuery.data?.rows ?? []}
            isLoading={weatherImpactQuery.isLoading}
            emptyMessage="No weather holds in this range"
            initialSortKey="bucket"
            initialSortDir="asc"
            columns={weatherColumns('Month', profitabilityHidden)}
          />
          <ScorecardTable<WeatherImpactRow>
            rows={weatherTerritoryImpactQuery.data?.rows ?? []}
            isLoading={weatherTerritoryImpactQuery.isLoading}
            emptyMessage="No weather holds by territory"
            initialSortKey="bucket"
            initialSortDir="asc"
            columns={weatherColumns('Territory', profitabilityHidden)}
          />
        </div>
      </ReportSection>

      <ReportSection title="By contract">
        <ScorecardTable<BvaContractRow>
          rows={data?.byContract ?? []}
          isLoading={reportQuery.isLoading}
          emptyMessage="No completed budgeted visits in this range"
          initialSortKey="costVariance"
          columns={sharedColumns<BvaContractRow>(
            'label',
            'Contract',
            (r) => r.label,
            !profitabilityHidden,
          )}
        />
      </ReportSection>

      <ReportSection title="By property">
        <ScorecardTable<BvaPropertyRow>
          rows={data?.byProperty ?? []}
          isLoading={reportQuery.isLoading}
          emptyMessage="No completed budgeted visits in this range"
          initialSortKey="costVariance"
          columns={sharedColumns<BvaPropertyRow>(
            'property',
            'Property',
            (r) => r.property,
            !profitabilityHidden,
          )}
        />
      </ReportSection>

      <ReportSection
        title="Profitable services"
        description="Per production-kit roll-up across the range."
      >
        <ScorecardTable<BvaServiceRow>
          rows={data?.byService ?? []}
          isLoading={reportQuery.isLoading}
          emptyMessage="No production-kit attributed visits in this range"
          initialSortKey="margin"
          columns={sharedColumns<BvaServiceRow>(
            'kitName',
            'Service kit',
            (r) => r.kitName,
            !profitabilityHidden,
          )}
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function hasStoredCostingPeriod(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.localStorage.getItem(COSTING_PERIOD_STORAGE_KEY) !== null
  );
}

function currentQuarterRange(): { from: string; to: string } {
  const now = new Date();
  const quarterStartMonth = Math.floor(now.getUTCMonth() / 3) * 3;
  const from = new Date(Date.UTC(now.getUTCFullYear(), quarterStartMonth, 1, 0, 0, 0, 0));
  const to = new Date(Date.UTC(now.getUTCFullYear(), quarterStartMonth + 3, 0, 23, 59, 59, 999));
  return { from: from.toISOString(), to: to.toISOString() };
}

function resolveSeriesSeasonRange(
  jobs: JobListResponse['data'],
  now = new Date(),
): { from: string; to: string } | null {
  for (const job of jobs) {
    const start = parseSeasonMonthDay(job.recurringConfig?.seasonStart);
    const end = parseSeasonMonthDay(job.recurringConfig?.seasonEnd);
    if (!start || !end) continue;
    const window = seasonWindowForDate(start, end, now);
    if (now >= window.from && now <= window.to) {
      return {
        from: window.from.toISOString(),
        to: window.to.toISOString(),
      };
    }
  }
  return null;
}

function parseSeasonMonthDay(
  value: string | null | undefined,
): { month: number; day: number } | null {
  if (!value) return null;
  const match = value.match(/^(?:\d{4}-)?(\d{2})-(\d{2})$/);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (!Number.isInteger(month) || !Number.isInteger(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { month, day };
}

function seasonWindowForDate(
  start: { month: number; day: number },
  end: { month: number; day: number },
  now: Date,
): { from: Date; to: Date } {
  const year = now.getUTCFullYear();
  const startThisYear = seasonBoundary(year, start, 'start');
  const endThisYear = seasonBoundary(year, end, 'end');
  const wrapsYear = startThisYear.getTime() > endThisYear.getTime();
  if (!wrapsYear) {
    return { from: startThisYear, to: endThisYear };
  }
  if (now.getTime() <= endThisYear.getTime()) {
    return {
      from: seasonBoundary(year - 1, start, 'start'),
      to: endThisYear,
    };
  }
  return {
    from: startThisYear,
    to: seasonBoundary(year + 1, end, 'end'),
  };
}

function seasonBoundary(
  year: number,
  value: { month: number; day: number },
  edge: 'start' | 'end',
): Date {
  return edge === 'start'
    ? new Date(Date.UTC(year, value.month - 1, value.day, 0, 0, 0, 0))
    : new Date(Date.UTC(year, value.month - 1, value.day, 23, 59, 59, 999));
}
